import { useState } from 'react'
import { ArrowRight, Plus, Trash2, FileText, Globe, Database } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { KnowledgeItem } from './types'

interface BuilderPhaseKnowledgeProps {
  knowledgeItems: KnowledgeItem[]
  businessName: string
  websiteUrl: string
  scrapeStatus: string
  onAddItem: (item: KnowledgeItem) => void
  onRemoveItem: (id: string) => void
  onContinue: () => void
}

export default function BuilderPhaseKnowledge({
  knowledgeItems,
  businessName,
  websiteUrl,
  scrapeStatus,
  onAddItem,
  onRemoveItem,
  onContinue,
}: BuilderPhaseKnowledgeProps) {
  const [mode, setMode] = useState<'idle' | 'text' | 'url'>('idle')
  const [textName, setTextName] = useState('')
  const [textContent, setTextContent] = useState('')
  const [urlInput, setUrlInput] = useState('')

  const scrapedIncluded = scrapeStatus === 'complete' || scrapeStatus === 'preloaded'

  const handleAddText = () => {
    if (!textName.trim() || !textContent.trim()) return
    onAddItem({
      id: `kb-${Date.now()}`,
      name: textName.trim(),
      content: textContent.trim(),
      type: 'text',
    })
    setTextName('')
    setTextContent('')
    setMode('idle')
  }

  const handleAddUrl = () => {
    if (!urlInput.trim()) return
    onAddItem({
      id: `kb-url-${Date.now()}`,
      name: urlInput.trim(),
      content: '',
      type: 'url',
      url: urlInput.trim(),
    })
    setUrlInput('')
    setMode('idle')
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-medium">Knowledge Base</p>
        <p className="text-xs text-muted-foreground mt-1">
          Add information your AI agent can reference during calls — FAQs, policies, team bios, or additional pages.
        </p>
      </div>

      {/* Auto-included scraped content */}
      {scrapedIncluded && (
        <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
          <Database className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Website content auto-included</p>
            <p className="text-xs text-muted-foreground">
              Content scraped from {websiteUrl || businessName} will be automatically added to your agent's knowledge base.
            </p>
          </div>
        </div>
      )}

      {/* Added items list */}
      {knowledgeItems.length > 0 && (
        <div className="space-y-2">
          {knowledgeItems.map((item) => (
            <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg border">
              {item.type === 'url' ? (
                <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{item.name}</p>
                {item.type === 'text' && (
                  <p className="text-xs text-muted-foreground truncate">{item.content.slice(0, 80)}...</p>
                )}
              </div>
              <button
                onClick={() => onRemoveItem(item.id)}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add content */}
      {mode === 'idle' && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setMode('text')}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Text
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMode('url')}>
            <Globe className="h-3.5 w-3.5 mr-1.5" />
            Add URL
          </Button>
        </div>
      )}

      {mode === 'text' && (
        <div className="space-y-3 p-3 rounded-lg border">
          <Input
            value={textName}
            onChange={(e) => setTextName(e.target.value)}
            placeholder="Name (e.g. Fee Policy, Team Bios)"
            className="text-sm"
          />
          <Textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder="Paste your content here..."
            className="text-sm min-h-[100px] resize-none"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddText} disabled={!textName.trim() || !textContent.trim()}>
              Add
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setMode('idle'); setTextName(''); setTextContent('') }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'url' && (
        <div className="space-y-3 p-3 rounded-lg border">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/about"
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddUrl} disabled={!urlInput.trim()}>
              Add
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setMode('idle'); setUrlInput('') }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Button className="w-full" onClick={onContinue}>
        Continue
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  )
}
